// hooks/useMultiCarManagement.tsx
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { MultiCarItem } from "../../home/components/types";
import { addProduct, createCar } from "../utils/multiCar.utils";
import { useCarWashData } from "./useCarWashData";
import { Product } from "src/shared/services/interfacev2";

export const useMultiCarManagement = (showModalOnInit: boolean = false) => {
  const isFromPayment = sessionStorage.getItem("carWashFromPayment") === "true";

  const [multiCar, setMultiCar] = useState<MultiCarItem[]>(() => {
    if (isFromPayment) {
      const saved = localStorage.getItem("carWashMultiCar");
      return saved ? JSON.parse(saved) : [];
    }
    return [];
  });

  const { customerData } = useCarWashData();

  const [carNumber, setCarNumber] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(showModalOnInit);
  const [editCarIndex, setEditCarIndex] = useState<number | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [shouldPreserveData, setShouldPreserveData] = useState<boolean>(isFromPayment);

  useEffect(() => {
    if (isFromPayment) {
      sessionStorage.removeItem("carWashFromPayment");
    }
  }, [isFromPayment]);

  useEffect(() => {
    if (!customerData?.serviceDetails) return;

    if (shouldPreserveData) return;

    const customerCars: MultiCarItem[] = customerData.serviceDetails.map((service) => ({
      carNumber: service.carNumber,
      productDetails: (service.productDetails || []).map((p: any) => ({
        _id: p._id ?? { $oid: crypto.randomUUID() },

        productName: p.productName || p.product || "",
        amount: p.amount || p.price || "0",
        price: p.price || p.amount || "0",
      })) as unknown as Product[],
      totalPrice: service.totalPrice,
    }));

    setMultiCar(customerCars);

    setMultiCar(customerCars);
  }, [customerData, shouldPreserveData]);

  useEffect(() => {
    if (multiCar.length > 0) {
      localStorage.setItem("carWashMultiCar", JSON.stringify(multiCar));
    } else {
      localStorage.removeItem("carWashMultiCar");
    }
  }, [multiCar]);

  const addCar = () => {
    const trimmedCarNumber = carNumber.trim().toUpperCase();

    if (!trimmedCarNumber) {
      toast.error("Please enter a valid car number.");
      return;
    }

    const isDuplicate = multiCar.some((car) => car.carNumber?.toUpperCase() === trimmedCarNumber);

    if (isDuplicate) {
      toast.error("This car number is already added.");
      return;
    }

    setMultiCar((prev) => [...prev, createCar(trimmedCarNumber)]);

    setCarNumber("");
    setIsModalOpen(false);
    setEditCarIndex(null);
  };

  /** ✅ Remove Car */
  const removeCar = (carIndex: number) => {
    setMultiCar((prev) => prev.filter((_, index) => index !== carIndex));
  };

  /** ✅ Remove Product */
  const removeProductFromCar = (productToDelete: Product | null, carIndex: number) => {
    if (!productToDelete) return;

    setMultiCar((prev) => {
      const updatedCars = [...prev];
      const targetCar = updatedCars[carIndex];

      if (!targetCar) return prev;

      const updatedProducts = targetCar.productDetails.filter((p) => p._id?.$oid !== productToDelete._id?.$oid);

      const updatedTotalPrice = updatedProducts.reduce((sum, p) => sum + parseFloat(p.amount || "0"), 0);

      updatedCars[carIndex] = {
        ...targetCar,
        productDetails: updatedProducts,
        totalPrice: updatedTotalPrice,
      };

      return updatedCars;
    });
  };

  const addProductToCar = (product: Product, carIndex: number | null = null) => {
    setMultiCar((prev) => {
      const updatedCars = [...prev];
      const targetIndex = carIndex ?? updatedCars.length - 1;

      if (targetIndex < 0 || targetIndex >= updatedCars.length) {
        toast.warning("Please add a car first.");
        return prev;
      }

      const updatedCar = addProduct(updatedCars[targetIndex], product);

      if (!updatedCar) return prev;

      updatedCars[targetIndex] = updatedCar;
      return updatedCars;
    });
  };

  const calculateTotalPrice = () => multiCar.reduce((acc, car) => acc + car.totalPrice, 0).toFixed(2);
  const clearAllData = useCallback(() => {
    setMultiCar([]);
    setCarNumber("");
    setEditCarIndex(null);
    setIsEditing(false);
    setShouldPreserveData(false);

    localStorage.removeItem("carWashMultiCar");
    localStorage.removeItem("carWashPaymentMethod");
  }, []);

  const clearDataForPrint = useCallback(() => {
    sessionStorage.setItem("carWashPrintCompleted", "true");
    clearAllData();
  }, [clearAllData]);

  const clearDataForNavigationAway = useCallback(() => {
    sessionStorage.setItem("carWashFromDashboard", "true");
    clearAllData();
  }, [clearAllData]);

  const markGoingToPayment = useCallback(() => {
    sessionStorage.setItem("carWashFromPayment", "true");
  }, []);

  const handleNavigateAway = useCallback(() => {
    if (multiCar.length > 0) {
      clearDataForNavigationAway();
    }
  }, [multiCar.length, clearDataForNavigationAway]);

  const startEditingCar = (index: number) => {
    setEditCarIndex(index);
    setIsEditing(true);
  };

  const stopEditingCar = () => {
    setEditCarIndex(null);
    setIsEditing(false);
  };

  return {
    multiCar,
    carNumber,
    setCarNumber,
    isModalOpen,
    setIsModalOpen,
    editCarIndex,
    isEditing,
    addCar,
    removeCar,
    removeProductFromCar,
    addProductToCar,
    calculateTotalPrice,
    startEditingCar,
    stopEditingCar,
    clearAllData,
    clearDataForPrint,
    clearDataForNavigationAway,
    markGoingToPayment,
    handleNavigateAway,
    shouldPreserveData,
  };
};
